if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        /* The form intentionally carries `novalidate` for Dawn compatibility,
           so explicitly enforce the inventory-aware quantity bounds before
           sending FormData. This prevents avoidable partial-success 422s. */
        const quantityInput = this.form.elements.namedItem('quantity');
        if (quantityInput instanceof HTMLInputElement && !quantityInput.checkValidity()) {
          quantityInput.reportValidity();
          return;
        }

        this.error = false;
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;
        let addSucceeded = false;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (soldOutMessage) {
                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButtonText.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
              }
              this.error = true;
              /* Shopify can clamp a tracked overage into the cart and still
                 return 422. Reconcile from freshly rendered cart sections on
                 every add error; never infer mutation from English copy. */
              return this.reconcileCartAfterError();
            }

            addSucceeded = true;
            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            this.error = false;
            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            const cartUpdatePromise = publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            }).then(() => {
              CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
            });
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
            /* ProductInfo uses this event to fetch the new aggregate cart
               quantity and re-apply the effective stock ceiling. Keep the add
               control locked until those subscribers settle, otherwise a fast
               second click can use stale capacity. */
            return cartUpdatePromise;
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(window.cartStrings.error);
            this.error = false;
            return this.reconcileCartAfterError();
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (addSucceeded && this.cart?.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error && !this.submitButton.disabled) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      reconcileCartAfterError() {
        const drawer = document.querySelector('cart-drawer');
        const notification = drawer ? null : document.querySelector('cart-notification');
        const sectionIds = drawer
          ? ['cart-drawer', 'cart-icon-bubble']
          : notification?.getReconciliationSections?.().map((section) => section.id) || ['cart-icon-bubble'];
        const separator = window.routes.cart_url.includes('?') ? '&' : '?';
        const url = `${window.routes.cart_url}${separator}sections=${encodeURIComponent(sectionIds.join(','))}`;

        return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then((response) => {
            if (!response.ok) throw new Error(`Cart reconciliation failed: ${response.status}`);
            return response.json();
          })
          .then((sections) => {
            if (drawer?.reconcileContents) {
              drawer.reconcileContents({ sections });
            } else if (notification?.reconcileContents) {
              notification.reconcileContents({ sections });
            } else {
              const target = document.getElementById('cart-icon-bubble');
              const source = sections['cart-icon-bubble']
                ? new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section')
                : null;
              if (target && source) target.innerHTML = source.innerHTML;
            }
            return publish(PUB_SUB_EVENTS.cartUpdate, {
              source: drawer ? 'cart-drawer-items' : 'product-form-error-reconcile',
              cartData: null,
            });
          })
          .catch((error) => {
            console.error(error);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
          this.error = false;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
